import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Play, Eye, Clock, Monitor, Smartphone, AlertCircle, Timer } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  MODE_SELECT: 'mode_select',
  INSTRUCTIONS: 'instructions',
  PLAYING: 'playing',
  FINISHED: 'finished',
};

// Level configuration - subtle grey changes for professional adults
// Using lighter greys - base is light grey, darkens slightly on event
const LEVEL_CONFIG = {
  1: {
    name: 'Standard',
    baseGrey: 190,        // Light grey base
    darkGrey: 165,        // 25 point difference - noticeable but requires attention
    minInterval: 20000,   // 20-50 seconds between events (full mode)
    maxInterval: 50000,
    missWindow: 2500,     // 2.5 seconds to respond
  },
  2: {
    name: 'Moderate',
    baseGrey: 190,
    darkGrey: 172,        // 18 point difference
    minInterval: 22000,
    maxInterval: 55000,
    missWindow: 2300,
  },
  3: {
    name: 'Challenging',
    baseGrey: 190,
    darkGrey: 177,        // 13 point difference
    minInterval: 25000,
    maxInterval: 60000,
    missWindow: 2100,
  },
  4: {
    name: 'Difficult',
    baseGrey: 190,
    darkGrey: 181,        // 9 point difference - quite subtle
    minInterval: 28000,
    maxInterval: 65000,
    missWindow: 2000,
  },
  5: {
    name: 'Expert',
    baseGrey: 190,
    darkGrey: 184,        // Only 6 point difference - very subtle on light grey
    minInterval: 30000,
    maxInterval: 70000,
    missWindow: 1800,
  },
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Keyboard/Click</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch button</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Test Mode Selection (Full 30min or Quick 2min)
const TestModeSelector = ({ level, onSelect }) => {
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG[1];
  
  return (
    <Card className="border-slate-200">
      <CardContent className="pt-8">
        <div className="text-center mb-6">
          <Badge className="mb-3 bg-[#0B1D51]/10 text-[#0B1D51]">
            Level {level}: {config.name}
          </Badge>
          <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
            Choose Test Duration
          </h2>
          <p className="text-sm text-[#64748B]">
            The full test mirrors real assessment conditions
          </p>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          <Button
            variant="outline"
            className="h-24 flex-col gap-2 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
            onClick={() => onSelect('full')}
            data-testid="select-full-mode"
          >
            <div className="flex items-center gap-2">
              <Timer className="w-6 h-6 text-[#0B1D51]" />
              <span className="font-semibold text-lg">Full Test (30 minutes)</span>
            </div>
            <p className="text-xs text-[#64748B]">Authentic WAFV assessment duration - tests sustained concentration</p>
          </Button>
          
          <Button
            variant="outline"
            className="h-24 flex-col gap-2 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
            onClick={() => onSelect('quick')}
            data-testid="select-quick-mode"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-6 h-6 text-[#FF9F1C]" />
              <span className="font-semibold text-lg">Quick Practice (2 minutes)</span>
            </div>
            <p className="text-xs text-[#64748B]">Shorter practice session to learn the task</p>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Instructions Screen
const InstructionsScreen = ({ level, testMode, onStart }) => {
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG[1];
  const duration = testMode === 'full' ? '30 minutes' : '2 minutes';
  
  return (
    <Card className="border-slate-200">
      <CardContent className="pt-6">
        <div className="text-center mb-6">
          <Badge className="mb-3 bg-[#0B1D51]/10 text-[#0B1D51]">
            Level {level}: {config.name} | {duration}
          </Badge>
          <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
            Vigilance Test
          </h2>
          <p className="text-[#64748B]">
            Develops skills for WAFV Vigilance Assessment
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
            <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">1</div>
            <p className="text-sm text-[#64748B]">Watch the <strong>small grey square</strong> in the centre of the larger grey square.</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
            <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">2</div>
            <p className="text-sm text-[#64748B]">The <strong>inner square</strong> will occasionally become <strong>slightly darker</strong>.</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
            <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">3</div>
            <p className="text-sm text-[#64748B]">Click/tap <strong>immediately</strong> when you notice the inner square darken.</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
            <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">4</div>
            <p className="text-sm text-[#64748B]">Avoid clicking when no change has occurred (<strong>false alarms</strong>).</p>
          </div>
        </div>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">This is a test of sustained attention</span>
          </div>
          <p className="text-sm text-amber-600 mt-1">
            The task is deliberately monotonous. Maintaining focus over {duration} is the challenge.
            {level >= 4 && ' At this level, changes are very subtle - stay alert!'}
          </p>
        </div>

        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-6">
          <p className="text-sm text-blue-600">
            <strong>RIS-3751-TOM:</strong> Vigilance tests measure ability to maintain attention during monotonous tasks - critical for detecting rare but important signals during train operations.
          </p>
        </div>

        <Button
          onClick={onStart}
          className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white"
          data-testid="begin-test-btn"
        >
          <Play className="w-4 h-4 mr-2" />
          Begin Test ({duration})
        </Button>
      </CardContent>
    </Card>
  );
};

const VigilanceGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [testMode, setTestMode] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isDark, setIsDark] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [missed, setMissed] = useState(0);
  const [falseAlarms, setFalseAlarms] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [totalEvents, setTotalEvents] = useState(0);
  
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG[1];
  const startTime = useRef(Date.now());
  const darkStartTime = useRef(null);
  const eventTimeout = useRef(null);
  const missTimeout = useRef(null);
  const isDarkRef = useRef(false);
  const gameStateRef = useRef(gameState);
  
  // Keep ref in sync with state
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Schedule next darkening event
  const scheduleNextEvent = useCallback(() => {
    if (eventTimeout.current) clearTimeout(eventTimeout.current);
    
    // Quick mode: 5-15 second intervals (expect 8-12 events in 2 minutes)
    // Full mode: use level-based intervals from config
    let minInterval, maxInterval;
    if (testMode === 'quick') {
      minInterval = 5000;   // 5 seconds minimum
      maxInterval = 15000;  // 15 seconds maximum
    } else {
      minInterval = config.minInterval;
      maxInterval = config.maxInterval;
    }
    
    const nextEventIn = minInterval + Math.random() * (maxInterval - minInterval);
    
    eventTimeout.current = setTimeout(() => {
      // Use ref to check current game state (avoids stale closure)
      if (gameStateRef.current !== GAME_STATES.PLAYING) return;
      
      // Trigger darkening
      setIsDark(true);
      isDarkRef.current = true;
      darkStartTime.current = Date.now();
      setTotalEvents(e => e + 1);
      
      // Auto-miss after timeout (shorter in quick mode)
      const missTime = testMode === 'quick' ? 2500 : config.missWindow;
      missTimeout.current = setTimeout(() => {
        if (isDarkRef.current) {
          setMissed(m => m + 1);
          setScore(s => Math.max(0, s - 5));
          setIsDark(false);
          isDarkRef.current = false;
          setFeedback({ type: 'missed', message: 'MISSED!' });
          setTimeout(() => setFeedback(null), 800);
          scheduleNextEvent();
        }
      }, missTime);
      
    }, nextEventIn);
  }, [testMode, config]);

  // Handle user response
  const handleResponse = () => {
    if (gameState !== GAME_STATES.PLAYING) return;
    
    if (isDarkRef.current) {
      // Correct detection
      const reactionTime = Date.now() - darkStartTime.current;
      setReactionTimes(prev => [...prev, reactionTime]);
      setCorrect(c => c + 1);
      
      // Score based on reaction time
      let points = 10;
      if (reactionTime < 500) points = 15;
      else if (reactionTime < 1000) points = 12;
      else if (reactionTime > 2000) points = 5;
      
      setScore(s => s + points);
      setFeedback({ type: 'correct', message: `${reactionTime}ms` });
      
      // Reset
      setIsDark(false);
      isDarkRef.current = false;
      if (missTimeout.current) clearTimeout(missTimeout.current);
      
      setTimeout(() => {
        setFeedback(null);
        scheduleNextEvent();
      }, 500);
    } else {
      // False alarm
      setFalseAlarms(f => f + 1);
      setScore(s => Math.max(0, s - 3));
      setFeedback({ type: 'wrong', message: 'False alarm!' });
      setTimeout(() => setFeedback(null), 800);
    }
  };

  // Start game
  const startGame = () => {
    const duration = testMode === 'full' ? 30 * 60 : 2 * 60; // 30 min or 2 min
    setTimeLeft(duration);
    gameStateRef.current = GAME_STATES.PLAYING; // Set ref immediately
    setGameState(GAME_STATES.PLAYING);
    startTime.current = Date.now();
    
    // Schedule first event with a slight delay to ensure state is set
    setTimeout(() => {
      scheduleNextEvent();
    }, 100);
  };

  // Timer countdown
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameState(GAME_STATES.FINISHED);
          if (eventTimeout.current) clearTimeout(eventTimeout.current);
          if (missTimeout.current) clearTimeout(missTimeout.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (eventTimeout.current) clearTimeout(eventTimeout.current);
      if (missTimeout.current) clearTimeout(missTimeout.current);
    };
  }, []);

  // Finish game
  const finishGame = () => {
    const avgReaction = reactionTimes.length > 0 
      ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
      : 0;
    
    const accuracy = totalEvents > 0 ? Math.round((correct / totalEvents) * 100) : 0;
    
    onComplete({
      score: Math.max(0, score),
      maxScore: totalEvents * 15,
      accuracy,
      timeSpent: Math.round((Date.now() - startTime.current) / 1000),
      correct,
      missed,
      falseAlarms,
      avgReactionTime: avgReaction,
      totalEvents,
      testMode,
      level,
    });
  };

  // Render based on game state
  if (gameState === GAME_STATES.READY) {
    return (
      <div className="max-w-md mx-auto">
        <DeviceModeSelector onSelect={(mode) => {
          setDeviceMode(mode);
          setGameState(GAME_STATES.MODE_SELECT);
        }} />
      </div>
    );
  }

  if (gameState === GAME_STATES.MODE_SELECT) {
    return (
      <div className="max-w-md mx-auto">
        <TestModeSelector 
          level={level} 
          onSelect={(mode) => {
            setTestMode(mode);
            setGameState(GAME_STATES.INSTRUCTIONS);
          }} 
        />
      </div>
    );
  }

  if (gameState === GAME_STATES.INSTRUCTIONS) {
    return (
      <div className="max-w-lg mx-auto">
        <InstructionsScreen 
          level={level} 
          testMode={testMode} 
          onStart={startGame} 
        />
      </div>
    );
  }

  if (gameState === GAME_STATES.PLAYING) {
    // Outer square stays constant light grey
    const outerGrey = 210;  // Light grey for outer frame (constant)
    const outerColor = `rgb(${outerGrey}, ${outerGrey}, ${outerGrey})`;
    
    // Inner square changes based on level config
    const innerColor = isDark 
      ? `rgb(${config.darkGrey}, ${config.darkGrey}, ${config.darkGrey})`
      : `rgb(${config.baseGrey}, ${config.baseGrey}, ${config.baseGrey})`;
    
    return (
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <Badge variant="outline">Level {level}: {config.name}</Badge>
            <p className="text-xs text-[#64748B] mt-1">
              {testMode === 'full' ? 'Full Test' : 'Quick Practice'}
            </p>
          </div>
          <div className="text-right">
            <p className={`font-mono text-2xl font-bold ${timeLeft < 60 ? 'text-[#EF4444]' : 'text-[#0B1D51]'}`}>
              {formatTime(timeLeft)}
            </p>
            <p className="text-xs text-[#64748B]">Score: {score}</p>
          </div>
        </div>

        {/* Vigilance Area - Small grey square centered inside larger grey square */}
        <div 
          className="relative w-full aspect-video rounded-lg flex items-center justify-center cursor-pointer select-none bg-slate-100"
          onClick={handleResponse}
          data-testid="vigilance-area"
        >
          {/* Outer grey square (constant, frame) */}
          <div 
            className="w-80 h-80 rounded-sm flex items-center justify-center"
            style={{ backgroundColor: outerColor }}
            data-testid="vigilance-outer"
          >
            {/* Inner grey square (changes contrast - 1/4 size of outer) */}
            <div 
              className="w-40 h-40 rounded-sm transition-colors duration-75"
              style={{ backgroundColor: innerColor }}
              data-testid="vigilance-inner"
            />
          </div>
          
          {/* Feedback overlay */}
          {feedback && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg font-bold ${
                feedback.type === 'correct' ? 'bg-green-500 text-white' :
                feedback.type === 'missed' ? 'bg-red-500 text-white' :
                'bg-amber-500 text-white'
              }`}
            >
              {feedback.message}
            </motion.div>
          )}
        </div>

        {/* Instructions reminder */}
        <p className="text-center text-sm text-[#64748B] mt-4">
          Click/tap when the inner square darkens
        </p>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mt-4 text-center">
          <div className="p-2 bg-green-50 rounded">
            <p className="text-xs text-green-600">Detected</p>
            <p className="font-bold text-green-700">{correct}</p>
          </div>
          <div className="p-2 bg-red-50 rounded">
            <p className="text-xs text-red-600">Missed</p>
            <p className="font-bold text-red-700">{missed}</p>
          </div>
          <div className="p-2 bg-amber-50 rounded">
            <p className="text-xs text-amber-600">False</p>
            <p className="font-bold text-amber-700">{falseAlarms}</p>
          </div>
          <div className="p-2 bg-blue-50 rounded">
            <p className="text-xs text-blue-600">Events</p>
            <p className="font-bold text-blue-700">{totalEvents}</p>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === GAME_STATES.FINISHED) {
    const accuracy = totalEvents > 0 ? Math.round((correct / totalEvents) * 100) : 0;
    const avgReaction = reactionTimes.length > 0 
      ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
      : 0;

    return (
      <div className="max-w-lg mx-auto">
        <Card className="border-slate-200">
          <CardContent className="pt-8 text-center">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
              accuracy >= 80 ? 'bg-green-100' : accuracy >= 60 ? 'bg-amber-100' : 'bg-red-100'
            }`}>
              <Eye className={`w-10 h-10 ${
                accuracy >= 80 ? 'text-green-600' : accuracy >= 60 ? 'text-amber-600' : 'text-red-600'
              }`} />
            </div>

            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Vigilance Test Complete
            </h2>
            
            <div className="mb-4">
              <Badge variant="outline" className="mb-2">
                Level {level} | {testMode === 'full' ? '30 minutes' : '2 minutes'}
              </Badge>
            </div>

            <div className="text-4xl font-bold text-[#0B1D51] mb-2">
              {accuracy}%
            </div>
            <p className="text-[#64748B] mb-6">Detection Accuracy</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600">Detected</p>
                <p className="text-xl font-bold text-green-700">{correct}/{totalEvents}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600">Missed</p>
                <p className="text-xl font-bold text-red-700">{missed}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-600">False Alarms</p>
                <p className="text-xl font-bold text-amber-700">{falseAlarms}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600">Avg Reaction</p>
                <p className="text-xl font-bold text-blue-700">{avgReaction}ms</p>
              </div>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg mb-6 text-left">
              <p className="text-sm text-[#64748B]">
                <strong>Score:</strong> {score} points
              </p>
              <p className="text-sm text-[#64748B] mt-1">
                {accuracy >= 80 
                  ? 'Excellent sustained attention - you detected most changes accurately.'
                  : accuracy >= 60
                  ? 'Good performance, but some changes were missed. Practice maintaining focus.'
                  : 'Vigilance declined over time. This is normal but needs improvement for safety-critical roles.'}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setGameState(GAME_STATES.MODE_SELECT);
                  setScore(0);
                  setCorrect(0);
                  setMissed(0);
                  setFalseAlarms(0);
                  setTotalEvents(0);
                  setReactionTimes([]);
                }}
                data-testid="restart-btn"
              >
                Try Again
              </Button>
              <Button
                className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white"
                onClick={finishGame}
                data-testid="finish-btn"
              >
                Save Score
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default VigilanceGame;
